/**
 * Helpers PUROS de las pantallas de competencia: agrupar el fixture, nombrar
 * las rondas de una llave, resolver "el ganador de cuartos 2" y formatear un
 * resultado.
 *
 * Están acá y no adentro de los componentes por la misma razón de siempre en
 * este portal: lo que se puede testear sin DOM se testea sin DOM. Ninguna de
 * estas funciones sabe de React ni de idiomas — devuelven datos o CLAVES de
 * traducción, nunca texto armado a mano.
 */
import type { Competition, CompetitionStatus, FixtureMatch, FixtureStatus } from '@scout/core';
import type { MessageKey } from '../i18n/index';

// ---------------------------------------------------------------------------
// Agrupaciones
// ---------------------------------------------------------------------------

export interface MatchdayGroup {
  matchday: number;
  entries: FixtureMatch[];
}

/** El fixture por fecha, en orden. Es como se lee un calendario. */
export function groupByMatchday(fixture: readonly FixtureMatch[]): MatchdayGroup[] {
  const byDay = new Map<number, FixtureMatch[]>();
  for (const entry of fixture) {
    const acc = byDay.get(entry.matchday) ?? [];
    acc.push(entry);
    byDay.set(entry.matchday, acc);
  }
  return [...byDay.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([matchday, entries]) => ({ matchday, entries }));
}

export interface BracketRound {
  round: number;
  totalRounds: number;
  entries: FixtureMatch[];
}

/** Los cruces de una llave, ronda por ronda (cuartos → semis → final). */
export function bracketRounds(fixture: readonly FixtureMatch[]): BracketRound[] {
  const byRound = new Map<number, FixtureMatch[]>();
  let totalRounds = 0;
  for (const entry of fixture) {
    if (!entry.bracket) continue;
    totalRounds = Math.max(totalRounds, entry.bracket.totalRounds);
    const acc = byRound.get(entry.bracket.round) ?? [];
    acc.push(entry);
    byRound.set(entry.bracket.round, acc);
  }
  return [...byRound.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([round, entries]) => ({
      round,
      totalRounds,
      entries: [...entries].sort(
        (a, b) => (a.bracket?.position ?? 0) - (b.bracket?.position ?? 0),
      ),
    }));
}

/** ¿La fase se dibuja como llave o como calendario? */
export function isBracket(fixture: readonly FixtureMatch[]): boolean {
  return fixture.length > 0 && fixture.every((e) => e.bracket !== undefined);
}

// ---------------------------------------------------------------------------
// Nombres
// ---------------------------------------------------------------------------

/**
 * Cómo se llama una ronda, según cuántas faltan para la final. Devuelve la
 * CLAVE de traducción: "Cuartos de final" en el diccionario, no acá.
 */
export function roundLabelKey(round: number, totalRounds: number): MessageKey {
  const faltan = totalRounds - round;
  if (faltan === 0) return 'competition.roundFinal';
  if (faltan === 1) return 'competition.roundSemi';
  if (faltan === 2) return 'competition.roundQuarter';
  if (faltan === 3) return 'competition.roundOf16';
  return 'competition.roundNumber';
}

/** Qué mostrar en un lado del cruce. */
export type SlotLabel =
  | { kind: 'team'; teamId: string; name: string }
  /** Todavía no se sabe: sale del ganador (o del perdedor) de otro partido. */
  | { kind: 'winner' | 'loser'; fixtureId: string; round: number | null; position: number | null }
  /** Ni equipo ni fuente: una fecha cargada a mano sin rival todavía. */
  | { kind: 'empty' };

export function teamNames(competition: Competition | null): Map<string, string> {
  return new Map((competition?.teams ?? []).map((t) => [t.teamId, t.name]));
}

/**
 * Resuelve un lado del cruce a algo mostrable. Si el equipo está definido, su
 * nombre; si no, de dónde va a salir. Nunca devuelve un id crudo cuando puede
 * evitarlo, pero **prefiere el id al vacío**: una fila en blanco en el fixture
 * parece un error del sistema.
 */
export function slotLabel(
  entry: FixtureMatch,
  side: 'home' | 'away',
  names: Map<string, string>,
  byId: Map<string, FixtureMatch>,
): SlotLabel {
  const teamId = side === 'home' ? entry.homeTeamId : entry.awayTeamId;
  if (teamId !== undefined) {
    return { kind: 'team', teamId, name: names.get(teamId) ?? teamId };
  }
  const source = side === 'home' ? entry.homeFrom : entry.awayFrom;
  if (source) {
    const feeder = byId.get(source.fixtureId);
    return {
      kind: source.from,
      fixtureId: source.fixtureId,
      round: feeder?.bracket?.round ?? null,
      position: feeder?.bracket?.position ?? null,
    };
  }
  return { kind: 'empty' };
}

// ---------------------------------------------------------------------------
// Resultado
// ---------------------------------------------------------------------------

/** `3-1`, o `null` si el partido todavía no tiene resultado. */
export function scoreLabel(entry: FixtureMatch): string | null {
  if (!entry.result) return null;
  return `${entry.result.setsHome}-${entry.result.setsAway}`;
}

/** `25-20, 23-25, 25-18` — los parciales, que es lo que se discute. */
export function setsLabel(entry: FixtureMatch): string {
  if (!entry.result || entry.result.sets.length === 0) return '';
  return entry.result.sets.map((set) => `${set.home}-${set.away}`).join(', ');
}

/**
 * Clave de traducción del estado de la COMPETENCIA (borrador / en curso /
 * terminada). Distinto del estado de un partido del fixture, que es el de
 * abajo: son dos vocabularios que en pantalla quedan cerca y no hay que
 * mezclar.
 */
export function competitionStatusLabelKey(status: CompetitionStatus): MessageKey {
  if (status === 'active') return 'competition.statusActive';
  if (status === 'finished') return 'competition.statusFinished';
  return 'competition.statusDraft';
}

/** Clave de traducción del estado de un partido del fixture. */
export function statusLabelKey(status: FixtureStatus): MessageKey {
  switch (status) {
    case 'live':
      return 'competition.statusLive';
    case 'played':
      return 'competition.statusPlayed';
    case 'walkover':
      return 'competition.statusWalkover';
    case 'postponed':
      return 'competition.statusPostponed';
    case 'cancelled':
      return 'competition.statusCancelled';
    default:
      return 'competition.statusScheduled';
  }
}

/** Clave de traducción de la procedencia del resultado (de dónde salió). */
export function originLabelKey(origin: 'log' | 'manual' | 'walkover'): MessageKey {
  if (origin === 'log') return 'competition.originLog';
  if (origin === 'manual') return 'competition.originManual';
  return 'competition.originWalkover';
}

/** Cociente para mostrar: `null` (sin sets perdidos) se escribe como `∞`. */
export function ratioLabel(value: number | null): string {
  return value === null ? '∞' : value.toFixed(2);
}

/** Clave del criterio de desempate, para explicar por qué un equipo está arriba. */
export function tiebreakLabelKey(criterion: string): MessageKey {
  switch (criterion) {
    case 'points':
      return 'competition.tbPoints';
    case 'matchesWon':
      return 'competition.tbMatchesWon';
    case 'setRatio':
      return 'competition.tbSetRatio';
    case 'setDiff':
      return 'competition.tbSetDiff';
    case 'pointRatio':
      return 'competition.tbPointRatio';
    case 'pointDiff':
      return 'competition.tbPointDiff';
    case 'headToHead':
      return 'competition.tbHeadToHead';
    case 'setsWon':
      return 'competition.tbSetsWon';
    case 'pointsScored':
      return 'competition.tbPointsScored';
    default:
      return 'competition.tbPoints';
  }
}

/** Fases que tienen tabla (el playoff tiene llave, no tabla). */
export function phasesWithTable(competition: Competition | null): Competition['phases'] {
  return (competition?.phases ?? []).filter((p) => p.kind !== 'playoff');
}
